/**
 * Scope 3 Submission Upload Dialog
 *
 * Redesigned to match Scope 1/2 UI with stepper and month/year selectors
 */

import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  Divider,
} from '@mui/material';
import {
  CloudUpload,
  InsertDriveFile,
  Close,
  Download,
  ArrowForward,
  ArrowBack,
  LocationOn,
  Category,
} from '@mui/icons-material';
import { useApp } from '../../contexts/AuthContext';
import {
  scope3SubmissionService,
  SCOPE3_CATEGORIES,
  Scope3Submission,
} from '../../services/scope3SubmissionService';

interface Scope3SubmissionUploadProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (submission: Scope3Submission) => void;
  preselectedCategory?: number;
}

const steps = ['Select Details', 'Upload Data', 'Confirm'];

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const YEARS = [2020, 2021, 2022, 2023, 2024, 2025, 2026];

export const Scope3SubmissionUpload: React.FC<Scope3SubmissionUploadProps> = ({
  open,
  onClose,
  onSuccess,
  preselectedCategory,
}) => {
  const { facilities } = useApp();
  const currentDate = new Date();

  const [activeStep, setActiveStep] = useState(0);
  const [categoryNumber, setCategoryNumber] = useState<number>(preselectedCategory || 0);
  const [facilityId, setFacilityId] = useState<string>('');
  const [year, setYear] = useState<number>(currentDate.getFullYear());
  const [month, setMonth] = useState<number>(currentDate.getMonth() + 1);
  const [file, setFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);

  // Reset form when dialog opens/closes
  React.useEffect(() => {
    if (open) {
      setActiveStep(0);
      setCategoryNumber(preselectedCategory || 0);
      setFacilityId(facilities[0]?.id || '');
      setYear(currentDate.getFullYear());
      setMonth(currentDate.getMonth() + 1);
      setFile(null);
      setError(null);
    }
  }, [open, preselectedCategory, facilities]);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      validateAndSetFile(e.dataTransfer.files[0]);
    }
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      validateAndSetFile(e.target.files[0]);
    }
  };

  const validateAndSetFile = (selectedFile: File) => {
    const allowedExtensions = ['.xlsx', '.xls', '.csv'];
    const extension = selectedFile.name.toLowerCase().substring(selectedFile.name.lastIndexOf('.'));

    if (!allowedExtensions.includes(extension)) {
      setError('Please upload an Excel (.xlsx, .xls) or CSV file');
      return;
    }

    if (selectedFile.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      return;
    }

    setFile(selectedFile);
    setError(null);
  };

  const handleDownloadTemplate = async () => {
    if (!categoryNumber) {
      setError('Please select a category first');
      return;
    }

    setIsDownloadingTemplate(true);
    setError(null);

    try {
      const { url, filename } = await scope3SubmissionService.downloadTemplate(categoryNumber);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.message || 'Failed to download template');
    } finally {
      setIsDownloadingTemplate(false);
    }
  };

  const handleNext = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep(activeStep + 1);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  const handleSubmit = async () => {
    if (!categoryNumber || !facilityId || !file) {
      setError('Please fill in all required fields');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Format reporting period as YYYY-MM
      const reportingPeriod = `${year}-${String(month).padStart(2, '0')}`;

      const submission = await scope3SubmissionService.uploadSubmission({
        facility_id: facilityId,
        category_number: categoryNumber,
        reporting_period: reportingPeriod,
        file,
      });

      if (onSuccess) {
        onSuccess(submission);
      }
      handleClose();
    } catch (err: any) {
      setError(err.message || 'Failed to upload submission');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setActiveStep(0);
    setCategoryNumber(0);
    setFacilityId('');
    setYear(currentDate.getFullYear());
    setMonth(currentDate.getMonth() + 1);
    setFile(null);
    setError(null);
    onClose();
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const canProceed = () => {
    if (activeStep === 0) {
      return categoryNumber > 0 && facilityId;
    }
    if (activeStep === 1) {
      return file !== null;
    }
    return true;
  };

  const getSelectedCategoryName = () => {
    const cat = SCOPE3_CATEGORIES.find(c => c.number === categoryNumber);
    return cat ? `${cat.number}. ${cat.name}` : '';
  };

  const getSelectedFacilityName = () => {
    const fac = facilities.find(f => f.id === facilityId);
    return fac?.name || '';
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2, overflow: 'hidden', maxHeight: '85vh' }
      }}
    >
      {/* Header */}
      <Box sx={{
        px: 2, py: 1.5,
        backgroundColor: '#f3e8ff',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Box>
          <Typography variant="body1" sx={{ fontWeight: 600, color: '#111827', fontSize: '0.95rem' }}>
            Upload Scope 3 Data
          </Typography>
          <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.7rem' }}>
            Value chain emissions from upstream and downstream activities
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <Close sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {/* Stepper */}
      <Box sx={{ px: 2, py: 1.25, backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label, index) => (
            <Step key={label} completed={index < activeStep}>
              <StepLabel
                sx={{
                  '& .MuiStepLabel-label': {
                    fontSize: '0.65rem',
                    color: index <= activeStep ? '#111827' : '#9ca3af',
                    fontWeight: index === activeStep ? 600 : 400,
                  },
                  '& .MuiStepIcon-root': {
                    color: index < activeStep ? '#9333ea' : index === activeStep ? '#9333ea' : '#e5e7eb',
                    fontSize: 20,
                  },
                }}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <DialogContent sx={{ p: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, fontSize: '0.75rem', py: 0.5 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Step 1: Select Details */}
        {activeStep === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Category Selection */}
            <FormControl fullWidth required size="small">
              <InputLabel sx={{ fontSize: '0.8rem' }}>Scope 3 Category</InputLabel>
              <Select
                value={categoryNumber}
                label="Scope 3 Category"
                onChange={(e) => setCategoryNumber(Number(e.target.value))}
                startAdornment={
                  <Category sx={{ color: '#9333ea', mr: 0.5, ml: -0.5, fontSize: 18 }} />
                }
                sx={{ fontSize: '0.8rem' }}
              >
                <MenuItem value={0} disabled sx={{ fontSize: '0.8rem' }}>
                  Select a category
                </MenuItem>
                {SCOPE3_CATEGORIES.map((cat) => (
                  <MenuItem key={cat.number} value={cat.number} sx={{ fontSize: '0.8rem', py: 0.75 }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                        {cat.number}. {cat.name}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Facility Selection */}
            <FormControl fullWidth required size="small">
              <InputLabel sx={{ fontSize: '0.8rem' }}>Facility</InputLabel>
              <Select
                value={facilityId}
                label="Facility"
                onChange={(e) => setFacilityId(e.target.value)}
                startAdornment={
                  <LocationOn sx={{ color: '#4caf50', mr: 0.5, ml: -0.5, fontSize: 18 }} />
                }
                sx={{ fontSize: '0.8rem' }}
              >
                {facilities.map((facility) => (
                  <MenuItem key={facility.id} value={facility.id} sx={{ fontSize: '0.8rem', py: 0.75 }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                        {facility.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                        {facility.type} • {facility.country}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Divider sx={{ my: 0.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                Reporting Period
              </Typography>
            </Divider>

            {/* Year and Month Selection */}
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <FormControl fullWidth required size="small">
                <InputLabel sx={{ fontSize: '0.8rem' }}>Year</InputLabel>
                <Select
                  value={year}
                  label="Year"
                  onChange={(e) => setYear(Number(e.target.value))}
                  sx={{ fontSize: '0.8rem' }}
                >
                  {YEARS.map((y) => (
                    <MenuItem key={y} value={y} sx={{ fontSize: '0.8rem', py: 0.5 }}>{y}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth required size="small">
                <InputLabel sx={{ fontSize: '0.8rem' }}>Month</InputLabel>
                <Select
                  value={month}
                  label="Month"
                  onChange={(e) => setMonth(Number(e.target.value))}
                  sx={{ fontSize: '0.8rem' }}
                >
                  {MONTHS.map((m) => (
                    <MenuItem key={m.value} value={m.value} sx={{ fontSize: '0.8rem', py: 0.5 }}>
                      {m.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Template Download */}
            {categoryNumber > 0 && (
              <Button
                variant="outlined"
                size="small"
                startIcon={isDownloadingTemplate ? <CircularProgress size={16} /> : <Download />}
                onClick={handleDownloadTemplate}
                disabled={isDownloadingTemplate}
                sx={{
                  alignSelf: 'flex-start',
                  textTransform: 'none',
                  fontSize: '0.75rem',
                  borderColor: '#e5e7eb',
                  color: '#6b7280',
                  '&:hover': { borderColor: '#9333ea', color: '#9333ea' },
                }}
              >
                Download Template
              </Button>
            )}
          </Box>
        )}

        {/* Step 2: Upload File */}
        {activeStep === 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '0.8rem', mb: 1 }}>
              Upload your data file for <strong>{getSelectedCategoryName()}</strong>
            </Typography>

            <Paper
              variant="outlined"
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              sx={{
                p: 3,
                textAlign: 'center',
                backgroundColor: dragActive ? '#faf5ff' : '#fafafa',
                borderStyle: 'dashed',
                borderWidth: 2,
                borderColor: dragActive ? '#9333ea' : '#e5e7eb',
                borderRadius: 2,
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: '#9333ea',
                  backgroundColor: '#faf5ff',
                },
              }}
            >
              {file ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
                  <InsertDriveFile sx={{ color: '#9333ea', fontSize: 32 }} />
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem' }}>
                      {file.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                      {formatFileSize(file.size)}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFile(null);
                    }}
                    sx={{ ml: 1 }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <label style={{ cursor: 'pointer', display: 'block' }}>
                  <input
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                  />
                  <CloudUpload sx={{ fontSize: 40, color: '#9ca3af', mb: 1 }} />
                  <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '0.85rem', mb: 0.5 }}>
                    Drop your file here or click to browse
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.7rem' }}>
                    Excel (.xlsx, .xls) or CSV • Max 10MB
                  </Typography>
                </label>
              )}
            </Paper>
          </Box>
        )}

        {/* Step 3: Confirm */}
        {activeStep === 2 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '0.8rem', mb: 1 }}>
              Review your submission details
            </Typography>

            <Paper sx={{ p: 2, backgroundColor: '#f9fafb', borderRadius: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.7rem' }}>Category</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>{getSelectedCategoryName()}</Typography>
                </Box>
                <Divider />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.7rem' }}>Facility</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>{getSelectedFacilityName()}</Typography>
                </Box>
                <Divider />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.7rem' }}>Period</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                    {MONTHS.find(m => m.value === month)?.label} {year}
                  </Typography>
                </Box>
                <Divider />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.7rem' }}>File</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <InsertDriveFile sx={{ fontSize: 16, color: '#9333ea' }} />
                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>{file?.name}</Typography>
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Box>
        )}
      </DialogContent>

      {/* Footer Actions */}
      <Box sx={{
        px: 2, py: 1.5,
        borderTop: '1px solid #e5e7eb',
        display: 'flex',
        justifyContent: 'space-between',
        backgroundColor: '#f9fafb',
      }}>
        <Button
          onClick={activeStep === 0 ? handleClose : handleBack}
          disabled={isLoading}
          startIcon={activeStep > 0 ? <ArrowBack sx={{ fontSize: 16 }} /> : undefined}
          sx={{
            textTransform: 'none',
            fontSize: '0.8rem',
            color: '#6b7280',
          }}
        >
          {activeStep === 0 ? 'Cancel' : 'Back'}
        </Button>

        {activeStep < steps.length - 1 ? (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={!canProceed()}
            endIcon={<ArrowForward sx={{ fontSize: 16 }} />}
            sx={{
              textTransform: 'none',
              fontSize: '0.8rem',
              backgroundColor: '#9333ea',
              boxShadow: 'none',
              '&:hover': { backgroundColor: '#7c3aed', boxShadow: 'none' },
            }}
          >
            Continue
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <CloudUpload sx={{ fontSize: 16 }} />}
            sx={{
              textTransform: 'none',
              fontSize: '0.8rem',
              backgroundColor: '#9333ea',
              boxShadow: 'none',
              '&:hover': { backgroundColor: '#7c3aed', boxShadow: 'none' },
            }}
          >
            {isLoading ? 'Uploading...' : 'Upload Data'}
          </Button>
        )}
      </Box>
    </Dialog>
  );
};
